import { COMPLAINT_INTENTS, type MentionRow } from './generator';

/**
 * The row cuts a module takes *below* its brand scope and its chip filter.
 *
 * A cut is not a filter. The chips answer "which conversations is the user
 * asking about"; these answer "which conversations can this card be about at
 * all" — a card about community threads cannot be about a row that was not
 * posted in a community, whatever the chips say. Each corresponds to a
 * nullable field on `MentionRow`, or to a named family of one.
 *
 * Lifted out of `CommunityView` on 2026-09-18 when Overview began drawing two
 * of its cards. They live in the data layer rather than beside one view because
 * the property that matters is that two cards reading "community rows" are
 * reading the *same* rows — the same reason `standings.ts` moved.
 */

/** Rows posted inside a monitored community. See `MentionRow.community`. */
export function inCommunity(rows: readonly MentionRow[]): MentionRow[] {
  return rows.filter((row) => row.community !== null);
}

/** Rows posted by one of the named accounts. See `MentionRow.voice`. */
export function byVoice(rows: readonly MentionRow[]): MentionRow[] {
  return rows.filter((row) => row.voice !== null);
}

/**
 * Earned media only. A mention with no outlet is somebody talking about us, not
 * somebody publishing about us, and the difference is the whole Media module.
 *
 * Lifted out of `MediaCards` on 2026-09-24 when the AI summary panel became the
 * second reader of it: a summary of a Media card has to cut its rows exactly as
 * the card does, and two copies of `row.outlet !== null` is two places for that
 * to stop being true. The cut is idempotent, so applying it twice is harmless.
 */
export function coverage(rows: readonly MentionRow[]): MentionRow[] {
  return rows.filter((row) => row.outlet !== null);
}

/**
 * Rows in the complaint *family*, not the `Complaint` chip alone. See
 * `COMPLAINT_INTENTS` — a billing issue, an outage report, a refund request and
 * a cancellation are all somebody telling us something is wrong.
 */
export function complaints(rows: readonly MentionRow[]): MentionRow[] {
  return rows.filter((row) => COMPLAINT_INTENTS.includes(row.intent));
}
